import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Col, Container, Form, Row, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClock,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";
import api from "../api/API";
import TaskModal from "../components/Modals/TaskModal";
import { useAuth } from "../hooks/useAuth";
import { useWorkspace } from "../context/WorkspaceContext";
import { getTaskPriorityLabel } from "../utils/taskPriority";
import "./MeuDiaPage.css";

const toISODate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const parseLocalDate = (value) => {
  if (!value) return null;

  if (typeof value === "string") {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (match) {
      const [, year, month, day] = match;
      return new Date(Number(year), Number(month) - 1, Number(day), 0, 0, 0, 0);
    }
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
};

const getInitials = (name = "") => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "EU";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const getPriorityLabel = (task) => getTaskPriorityLabel(task?.prioridade ?? task?.prioridade_label ?? task?.prioridade_nome);

const getDateStatus = (dateStr) => {
  if (!dateStr) return "future";
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const date = parseLocalDate(dateStr);
  if (!date) return "future";
  date.setHours(0, 0, 0, 0);

  if (date < today) return "overdue";
  if (date.getTime() === today.getTime()) return "today";
  return "future";
};

const getDefaultDateByBucket = (bucket) => {
  const today = new Date();

  switch (bucket) {
    case "today":
      return toISODate(today);
    case "overdue":
      today.setDate(today.getDate() - 1);
      return toISODate(today);
    case "week":
      today.setDate(today.getDate() + 3);
      return toISODate(today);
    default:
      return "";
  }
};

const getStageLabel = (task) =>
  task?.id_etapa_nome || task?.etapa || task?.etapa_nome || "";

const getDueLabel = (task) => {
  if (!task.vencimento) return "Sem prazo";
  const dueDate = parseLocalDate(task.vencimento);
  if (!dueDate) return "Sem prazo";
  return dueDate.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const TaskGroup = ({ title, bucketKey, tasks, onAddClick, onTaskClick, onToggleDone }) => (
  <Card className="meudia-group-card">
    <Card.Body>
      <div className="meudia-group-header">
        <div>
          <h5>{title}</h5>
          <span>{tasks.length} tarefa(s)</span>
        </div>

        <Button variant="outline-dark" size="sm" onClick={() => onAddClick(bucketKey)}>
          <FontAwesomeIcon icon={faPlus} />
        </Button>
      </div>

      {tasks.length === 0 ? (
        <div className="meudia-group-empty">Nenhuma tarefa neste grupo.</div>
      ) : (
        <div className="meudia-task-list">
          {tasks.map((task) => (
            <div key={task.id} className="meudia-task-card" role="button" onClick={() => onTaskClick(task)}>
              <div className="meudia-task-main">
                <div className="meudia-task-top">
                  <Form.Check
                    type="checkbox"
                    checked={!!task.concluido}
                    onClick={(event) => event.stopPropagation()}
                    onChange={() => onToggleDone(task)}
                  />

                  <div className="meudia-task-title-block">
                    <strong>{task.descricao}</strong>
                    <div className="meudia-task-badges">
                      <Badge bg={getDateStatus(task.vencimento) === "overdue" ? "danger" : getDateStatus(task.vencimento) === "today" ? "warning" : "secondary"}>
                        {getDueLabel(task)}
                      </Badge>
                      <Badge bg="light" text="dark">{getPriorityLabel(task)}</Badge>
                      {task.projeto ? <Badge bg="dark">{task.projeto}</Badge> : <Badge bg="warning" text="dark">Inbox</Badge>}
                      {getStageLabel(task) && <Badge bg="light" text="dark">{getStageLabel(task)}</Badge>}
                      {task.aguardando_terceiros && <Badge bg="info">Aguardando</Badge>}
                      {!task.id_usuario_responsavel && <Badge bg="secondary">Sem dono</Badge>}
                    </div>
                  </div>
                </div>

                {task.detalhe && <p className="meudia-task-detail">{task.detalhe}</p>}

                <div className="meudia-task-footer">
                  <span>
                    <FontAwesomeIcon icon={faClock} /> {getDueLabel(task)}
                  </span>
                  <span>{task.responsavel || "Sem responsavel"}</span>
                </div>
              </div>

              <div className="meudia-task-avatar">{getInitials(task.responsavel || "EU")}</div>
            </div>
          ))}
        </div>
      )}
    </Card.Body>
  </Card>
);

export default function MeuDiaPage() {
  const { user } = useAuth();
  const { selectedWorkspaceId, selectedWorkspace, loading: loadingWorkspace, error: workspaceError } = useWorkspace();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [defaultProject, setDefaultProject] = useState(null);
  const [defaultDueDate, setDefaultDueDate] = useState("");
  const [showSubtasks, setShowSubtasks] = useState(false);
  const [selectedProject, setSelectedProject] = useState("all");
  const [onlyMine, setOnlyMine] = useState(true);
  const [onlyWithoutOwner, setOnlyWithoutOwner] = useState(false);
  const [onlyOverdue, setOnlyOverdue] = useState(false);

  async function fetchTasks() {
    if (!selectedWorkspaceId) {
      setTasks([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setErrorMessage("");

    try {
      const response = await api.get(`/tasks?workspaceId=${selectedWorkspaceId}`);
      const records = Array.isArray(response.data)
        ? response.data
        : response.data?.records || response.data?.data || [];
      setTasks(records);
    } catch (err) {
      console.error("Erro ao carregar tarefas:", err);
      setErrorMessage(
        err?.response?.data?.message ||
          err?.message ||
          "Nao foi possivel carregar as tarefas do workspace."
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchTasks();
  }, [selectedWorkspaceId]);

  const projectOptions = useMemo(() => {
    return Array.from(
      new Map(
        tasks
          .filter((task) => task.id_projeto && task.projeto)
          .map((task) => [String(task.id_projeto), task.projeto])
      ).entries()
    ).map(([value, label]) => ({ value, label }));
  }, [tasks]);

  const filteredTasks = useMemo(() => {
    return tasks.filter((task) => {
      if (!showSubtasks && task.id_tarefa !== null) return false;
      if (selectedProject !== "all" && String(task.id_projeto) !== selectedProject) return false;
      if (onlyMine && String(task.id_usuario_responsavel || "") !== String(user?.id || "")) return false;
      if (onlyWithoutOwner && task.id_usuario_responsavel) return false;
      if (onlyOverdue && getDateStatus(task.vencimento) !== "overdue") return false;
      return true;
    });
  }, [tasks, showSubtasks, selectedProject, onlyMine, onlyWithoutOwner, onlyOverdue, user]);

  const groups = useMemo(() => {
    const today = toISODate(new Date());
    const now = new Date();
    const weekLimit = new Date(now);
    weekLimit.setDate(now.getDate() + 7);

    const buckets = {
      inbox: [],
      overdue: [],
      today: [],
      week: [],
      waiting: [],
      done: [],
    };

    filteredTasks.forEach((task) => {
      if (task.concluido) return buckets.done.push(task);
      if (task.aguardando_terceiros) return buckets.waiting.push(task);

      if (!task.vencimento) return buckets.inbox.push(task);

      const due = parseLocalDate(task.vencimento);
      if (!due) return buckets.inbox.push(task);
      const dueISO = toISODate(due);

      if (dueISO < today) buckets.overdue.push(task);
      else if (dueISO === today) buckets.today.push(task);
      else if (due > now && due <= weekLimit) buckets.week.push(task);
      else buckets.inbox.push(task);
    });

    return buckets;
  }, [filteredTasks]);

  const metrics = useMemo(() => {
    const total = filteredTasks.filter((task) => !task.concluido).length;
    const overdue = filteredTasks.filter((task) => !task.concluido && getDateStatus(task.vencimento) === "overdue").length;
    const today = filteredTasks.filter((task) => !task.concluido && getDateStatus(task.vencimento) === "today").length;
    const withoutOwner = filteredTasks.filter((task) => !task.concluido && !task.id_usuario_responsavel).length;
    const inbox = filteredTasks.filter((task) => !task.concluido && (!task.id_projeto || !task.id_etapa || getStageLabel(task).toLowerCase() === "backlog")).length;
    return { total, overdue, today, withoutOwner, inbox };
  }, [filteredTasks]);

  async function toggleDone(task) {
    const nextDone = !task.concluido;
    setTasks((current) =>
      current.map((item) => (item.id === task.id ? { ...item, concluido: nextDone } : item))
    );

    try {
      await api.put(`/tasks/${task.id}`, {
        fields: {
          concluido: nextDone,
          id_workspace: task.id_workspace || selectedWorkspaceId,
        },
      });
    } catch (err) {
      console.error("Erro ao concluir tarefa:", err);
      setTasks((current) =>
        current.map((item) => (item.id === task.id ? { ...item, concluido: task.concluido } : item))
      );
      setErrorMessage(
        err?.response?.data?.message ||
          err?.message ||
          "Nao foi possivel atualizar a tarefa."
      );
    }
  }

  function openNewTask(bucket) {
    setEditingTask(null);
    setDefaultProject(bucket === "inbox" ? null : null);
    setDefaultDueDate(getDefaultDateByBucket(bucket));
    setShowModal(true);
  }

  function openEditTask(task) {
    setEditingTask(task);
    setDefaultProject(task.id_projeto);
    setDefaultDueDate(task.vencimento || "");
    setShowModal(true);
  }

  return (
    <>
      <Container fluid className="meudia-page">
        <section className="meudia-hero">
          <div>
            <p className="meudia-kicker">Execution board</p>
            <h1>Meu Dia</h1>
            <p className="meudia-subtitle">
              Execute o que importa agora, sem perder o controle das capturas, atrasos e tarefas sem dono.
            </p>
          </div>

          <div className="meudia-hero-side">
            <div className="meudia-pill">
              <span>Workspace ativo</span>
              <strong>{selectedWorkspace?.nome || "Nenhum selecionado"}</strong>
            </div>
          </div>
        </section>

        {workspaceError && <Alert variant="warning">{workspaceError}</Alert>}
        {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}

        {!selectedWorkspaceId && !loadingWorkspace ? (
          <Alert variant="info">Selecione um workspace no topo da aplicacao para carregar seu dia.</Alert>
        ) : loading ? (
          <div className="text-center mt-5">
            <Spinner animation="border" variant="dark" />
          </div>
        ) : (
          <>
            <Row className="g-3 meudia-metrics">
              <Col md={6} xl={2}>
                <Card className="meudia-metric-card">
                  <Card.Body>
                    <span>Ativas</span>
                    <strong>{metrics.total}</strong>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6} xl={2}>
                <Card className="meudia-metric-card warning">
                  <Card.Body>
                    <span>Inbox</span>
                    <strong>{metrics.inbox}</strong>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6} xl={2}>
                <Card className="meudia-metric-card danger">
                  <Card.Body>
                    <span>Atrasadas</span>
                    <strong>{metrics.overdue}</strong>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6} xl={2}>
                <Card className="meudia-metric-card">
                  <Card.Body>
                    <span>Hoje</span>
                    <strong>{metrics.today}</strong>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6} xl={2}>
                <Card className="meudia-metric-card neutral">
                  <Card.Body>
                    <span>Sem dono</span>
                    <strong>{metrics.withoutOwner}</strong>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6} xl={2}>
                <Card className="meudia-metric-card success">
                  <Card.Body>
                    <span>Concluidas</span>
                    <strong>{groups.done.length}</strong>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Card className="meudia-toolbar">
              <Card.Body>
                <div className="meudia-toolbar-top">
                  <div>
                    <h5 className="mb-1">Filtro rapido de execucao</h5>
                    <p className="text-muted mb-0">
                      Reduza ruido e foque no que precisa de decisao agora.
                    </p>
                  </div>

                  <Button variant="dark" onClick={() => openNewTask("inbox")}>
                    <FontAwesomeIcon icon={faPlus} /> Capturar demanda
                  </Button>
                </div>

                <div className="meudia-filters">
                  <Form.Select
                    value={selectedProject}
                    onChange={(event) => setSelectedProject(event.target.value)}
                  >
                    <option value="all">Todos os projetos</option>
                    {projectOptions.map((project) => (
                      <option key={project.value} value={project.value}>
                        {project.label}
                      </option>
                    ))}
                  </Form.Select>

                  <Form.Check
                    type="switch"
                    id="switch-subtasks"
                    label={showSubtasks ? "Mostrar subtarefas" : "Ocultar subtarefas"}
                    checked={showSubtasks}
                    onChange={(e) => setShowSubtasks(e.target.checked)}
                  />

                  <Form.Check
                    type="switch"
                    id="switch-mine"
                    label="Somente minhas"
                    checked={onlyMine}
                    onChange={(e) => setOnlyMine(e.target.checked)}
                  />

                  <Form.Check
                    type="switch"
                    id="switch-owner"
                    label="So sem responsavel"
                    checked={onlyWithoutOwner}
                    onChange={(e) => setOnlyWithoutOwner(e.target.checked)}
                  />

                  <Form.Check
                    type="switch"
                    id="switch-overdue"
                    label="So atrasadas"
                    checked={onlyOverdue}
                    onChange={(e) => setOnlyOverdue(e.target.checked)}
                  />
                </div>
              </Card.Body>
            </Card>

            <div className="meudia-groups">
              <TaskGroup
                title="Inbox / Backlog"
                bucketKey="inbox"
                tasks={groups.inbox}
                onAddClick={openNewTask}
                onTaskClick={openEditTask}
                onToggleDone={toggleDone}
              />

              <TaskGroup
                title="Atrasadas"
                bucketKey="overdue"
                tasks={groups.overdue}
                onAddClick={openNewTask}
                onTaskClick={openEditTask}
                onToggleDone={toggleDone}
              />

              <TaskGroup
                title="Hoje"
                bucketKey="today"
                tasks={groups.today}
                onAddClick={openNewTask}
                onTaskClick={openEditTask}
                onToggleDone={toggleDone}
              />

              <TaskGroup
                title="Esta semana"
                bucketKey="week"
                tasks={groups.week}
                onAddClick={openNewTask}
                onTaskClick={openEditTask}
                onToggleDone={toggleDone}
              />

              <TaskGroup
                title="Aguardando terceiros"
                bucketKey="waiting"
                tasks={groups.waiting}
                onAddClick={openNewTask}
                onTaskClick={openEditTask}
                onToggleDone={toggleDone}
              />

              <TaskGroup
                title="Concluidas"
                bucketKey="done"
                tasks={groups.done}
                onAddClick={openNewTask}
                onTaskClick={openEditTask}
                onToggleDone={toggleDone}
              />
            </div>
          </>
        )}
      </Container>

      <TaskModal
        show={showModal}
        onClose={() => setShowModal(false)}
        task={editingTask}
        defaultProject={defaultProject}
        defaultDueDate={defaultDueDate}
        allowInboxTask
        onSaved={fetchTasks}
      />
    </>
  );
}
